import * as React from "react";
import { cn } from "@/v2/lib/utils";

/**
 * A selectable card button styled as a radio option: a dot indicator, an
 * optional icon, a title/description, and optional trailing content (e.g.
 * card-brand badges). Plain `<button>` + `<span>` (not the shadcn Radio
 * primitive) - the shadcn RadioGroupItem's SVG indicator relies on the
 * button's own padding box to center itself, which the .v2 button reset's
 * padding fallback can throw off at very small sizes; a plain span filled
 * with a background color has no such failure mode. Used for the gift
 * flow's delivery-option and payment-method rows, which repeated this exact
 * markup with only the icon/trailing content differing.
 */
export function RadioOptionCard({
  selected,
  onClick,
  icon,
  boxedIcon = true,
  compact = false,
  title,
  description,
  trailing,
  disabled,
  className,
}: {
  selected: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  /** Wrap `icon` in the tinted size-10 rounded-lg box (default). Pass false
   *  to render it bare and inline instead. */
  boxedIcon?: boolean;
  /** Smaller title text for single-line rows with no description. */
  compact?: boolean;
  title: React.ReactNode;
  description?: React.ReactNode;
  trailing?: React.ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex items-center justify-between gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer text-left w-full",
        selected ? "border-action bg-action/5" : "border-border bg-card",
        disabled && "opacity-50 cursor-not-allowed",
        className,
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          className={cn(
            "size-4 rounded-full border-2 shrink-0 transition-colors",
            selected ? "border-action bg-action" : "border-slate-300",
          )}
        />
        {icon &&
          (boxedIcon ? (
            <div className="size-10 rounded-lg bg-action/10 flex items-center justify-center shrink-0">
              {icon}
            </div>
          ) : (
            icon
          ))}
        <div className="min-w-0">
          <p
            className={cn(
              "font-bold text-foreground",
              compact ? "text-sm" : "text-base",
            )}
          >
            {title}
          </p>
          {description && (
            <p className="text-xs mt-0.5 text-muted-foreground">
              {description}
            </p>
          )}
        </div>
      </div>
      {trailing && (
        <div className="flex items-center gap-1.5 shrink-0">{trailing}</div>
      )}
    </button>
  );
}
